import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Col, Row, Tab } from 'react-bootstrap';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import ClusterViewModeSwitcher from '@/app/KeywordResearch/components/keyword-clustring/components/ClusterViewModeSwitcher';
import {
  altTags,
  density,
  htags,
  seoTitles,
  type SeoTitleRow
} from '@/app/Serp/config/contentBriefColumnConfig';
import { serpTooltips } from '@/app/Serp/tooltip/serpTooltips';
import type { SerpData, SerpItem } from '@/app/Serp/types/serpApiTypes';

type NavItemData = {
  id: string;
  name: string;
  icon: string;
  tooltip?: string;
  columns?: any;
  data?: any;
  columns_1?: any;
  columns_2?: any;
  data_1?: any;
  data_2?: any;
  stacked?: boolean;
};

interface CardTabsProps {
  serpData: SerpData;
}

const normalizeSerpUrl = (value: unknown) => {
  if (typeof value !== 'string' || !value.trim()) return '';
  let url = value.trim();
  try {
    url = decodeURIComponent(url);
  } catch {
    // آدرس از قبل دیکد شده است
  }
  return url.replace(/\/+$/, '').toLowerCase();
};

const seoTitlesWithRank = (
  rows: unknown,
  items: SerpItem[] | undefined
): SeoTitleRow[] => {
  const source = Array.isArray(rows) ? (rows as SeoTitleRow[]) : [];
  const rankByUrl = new Map<string, number>();
  for (const item of items ?? []) {
    const key = normalizeSerpUrl(item.url);
    const rank = item.rank_absolute;
    if (!key || typeof rank !== 'number' || rank <= 0) continue;
    if (!rankByUrl.has(key) || item.type === 'organic') {
      rankByUrl.set(key, rank);
    }
  }
  return source
    .map(row => ({
      ...row,
      rank: rankByUrl.get(normalizeSerpUrl(row.url))
    }))
    .sort(
      (a, b) =>
        (a.rank ?? Number.POSITIVE_INFINITY) -
        (b.rank ?? Number.POSITIVE_INFINITY)
    );
};

const CardTabs = ({ serpData }: CardTabsProps) => {
  const [activeTab, setActiveTab] = useState('seo');
  const { keyword: routeKeyword } = useParams<{ keyword?: string }>();
  const seoKeyword =
    (typeof serpData.keyword === 'string' && serpData.keyword) || routeKeyword;

  const seoTitleColumns = useMemo(
    () =>
      seoTitles.first.map(column =>
        column.field === 'seo_title'
          ? { ...column, cellRendererParams: { keyword: seoKeyword } }
          : column
      ),
    [seoKeyword]
  );
  const htagColumns = useMemo(
    () =>
      (htags?.first ?? []).map(column =>
        column.field === 'title'
          ? { ...column, cellRendererParams: { keyword: seoKeyword } }
          : column
      ),
    [seoKeyword]
  );
  const altColumns = useMemo(
    () =>
      altTags.map(column =>
        column.field === 'alt'
          ? { ...column, cellRendererParams: { keyword: seoKeyword } }
          : column
      ),
    [seoKeyword]
  );

  const navItems = useMemo<NavItemData[]>(
    () => [
      {
        id: 'seo',
        name: 'تحلیل عناوین سئو',
        icon: 'file-text',
        tooltip: serpTooltips?.grids?.seoTitles,
        columns_1: seoTitleColumns,
        columns_2: seoTitles.second,
        data_1: seoTitlesWithRank(
          serpData?.analysis_pages?.seo_titles_info,
          serpData?.items
        ),
        data_2: serpData?.analysis_pages?.seo_titles_density,
        stacked: true
      },
      {
        id: 'htags',
        name: 'تحلیل تگ های H1 تا H3',
        icon: 'text-paragraph',
        tooltip: serpTooltips?.grids?.headers,
        columns_1: htagColumns,
        columns_2: htags?.second,
        data_1: serpData?.analysis_pages?.htags_titles_info,
        data_2: serpData?.analysis_pages?.h_tags_density
      },
      {
        id: 'alts',
        name: 'متن های جایگزین تصاویر',
        icon: 'image',
        tooltip: serpTooltips.grids.imageAlts,
        columns: altColumns,
        data: serpData?.analysis_pages?.alt_images_info
      },
      {
        id: 'density',
        name: 'چگالی',
        icon: 'list-ul',
        tooltip: serpTooltips.grids.density,
        columns: density,
        data: serpData?.analysis_pages?.content_pages_density
      }
    ],
    [serpData, seoTitleColumns, htagColumns, altColumns]
  );

  return (
    <>
      <div className="my-3 d-flex justify-content-center">
        <ClusterViewModeSwitcher
          value={activeTab}
          onChange={setActiveTab}
          aria-label="تحلیل محتوا"
          modes={navItems.map(item => ({
            id: item.id,
            icon: item.icon,
            label: (
              <>
                {item.name}
                {item.tooltip ? (
                  <HelpTooltip placement="top" html={item.tooltip} />
                ) : null}
              </>
            )
          }))}
        />
      </div>
      <Card>
        <Tab.Container
          id="audience-tab"
          activeKey={activeTab}
          onSelect={key => key && setActiveTab(String(key))}
        >
          <Card.Body>
            <Row>
              <Col xxl={12}>
                <Tab.Content>
                  {navItems.map(item => (
                    <Tab.Pane key={item.id} unmountOnExit eventKey={item.id}>
                      {item.columns ? (
                        item.data && (
                          <AgGridWrapper
                            height="746px"
                            columns={item.columns}
                            data={item.data}
                          />
                        )
                      ) : (
                        <Row className="g-3">
                          <Col md={12} lg={item.stacked ? 12 : 6} className="mb-2">
                            <AgGridWrapper
                              height={item.stacked ? '520px' : '746px'}
                              columns={item.columns_1}
                              data={item.data_1}
                            />
                          </Col>
                          <Col md={12} lg={item.stacked ? 12 : 6} className="mb-2">
                            <AgGridWrapper
                              height="746px"
                              columns={item.columns_2}
                              data={item.data_2}
                            />
                          </Col>
                        </Row>
                      )}
                    </Tab.Pane>
                  ))}
                </Tab.Content>
              </Col>
            </Row>
          </Card.Body>
        </Tab.Container>
      </Card>
    </>
  );
};

export default CardTabs;
